<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { bsonId } from "@/common/utils/common";

import {
  svgIcon,
  baseInput,
  baseTextarea,
  baseSelect,
} from "@/common/components";

import { QuestionPayloadType, OptionType } from "@/common/types/question";

const { t, tm } = useI18n();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: QuestionPayloadType | null;
    title?: string;
    width?: string;
    remove?: string[];
  }>(),
  {
    width: "100%",
  },
);

const defaultData = {
  description: "",
  placeholder: null,
  metadata: null,
  name: "",
  type: "input",
  required: false,
};

const payload = ref<QuestionPayloadType>({ ...defaultData });
const options = ref<OptionType[]>([]);
const fileType = ref("");

const types = computed(
  () => tm("enums.question_type") as Record<string, string>,
);
const typesOptions = computed(() =>
  Object.entries(types.value)
    .map(([key, label]) => ({
      option: label,
      value: key,
    }))
    .filter((item) => !props.remove?.includes(item.value)),
);

const fileTypes = computed(
  () => tm("question.file_types") as Record<string, string>,
);
const fileTypesOptions = computed(() =>
  Object.entries(fileTypes.value).map(([key, label]) => ({
    option: label,
    value: key,
  })),
);

const requireOptions = computed(() => [
  {
    option: t("common.labels.yes"),
    value: true,
  },
  {
    option: t("common.labels.not"),
    value: false,
  },
]);

function update() {
  emit("update-data", payload.value);
}

function addOption() {
  options.value.push({
    id: bsonId(),
    option: "",
    value: null,
  });
  updateOptions();
}

function delOption(index: number) {
  options.value.splice(index, 1);
  updateOptions();
}

function updateOptions() {
  updateMetadata("options", options.value);
}

function updateMetadata(key: string, value: any) {
  payload.value.metadata = {
    ...payload.value.metadata,
    [key]: value,
  };
  update();
}

function updateFile(key: string) {
  const extensions = {
    document: ["doc", "docx", "xml", "pdf", "csv"],
    image: ["jpeg", "jpg"],
  };
  updateMetadata("file", {
    type: key,
    extension: extensions[key as never],
  });
}

function changeType(value: string) {
  payload.value.metadata = null;

  if (value === "file") {
    updateFile("document");
  } else {
    update();
  }
}

function fetchMetadata(metadata: any) {
  if (!metadata) return;

  if (typeof metadata === "string") {
    metadata = JSON.parse(metadata);
  }

  if (metadata?.options) options.value = metadata.options;

  if (metadata?.file) fileType.value = metadata.file.type;
}

watchEffect(() => {
  if (props.data) {
    payload.value = { ...props.data };

    if (props.data.metadata) {
      fetchMetadata(props.data.metadata);
    }
  }
});
</script>

<template>
  <section class="form-content">
    <div v-if="title" class="title">
      {{ title }}
    </div>
    <form>
      <div class="flex flex-row sm-flex-col md-flex-col gap-4">
        <base-select
          v-model="payload.type"
          :value="payload.type"
          :options="typesOptions"
          :is-empty="false"
          @change="changeType"
        >
          <template #label>
            {{ t("common.labels.type") }}
          </template>
        </base-select>

        <base-select
          v-model="payload.required"
          :value="payload.required"
          :options="requireOptions"
          :is-empty="false"
          @change="update()"
        >
          <template #label>
            {{ t("common.labels.required") }}
          </template>
        </base-select>
      </div>

      <base-input
        v-model="payload.name"
        :value="payload.name"
        :placeholder="t('common.placeholders.name')"
        @input="update()"
      >
        <template #label>
          {{ t("common.labels.name") }}
        </template>
      </base-input>

      <template v-if="payload.type === 'INPUT' || payload.type === 'TEXTAREA'">
        <base-input
          v-model="payload.placeholder"
          :value="payload.placeholder"
          @input="update()"
        >
          <template #label>
            {{ t("common.labels.example") }}
          </template>
        </base-input>
      </template>

      <base-textarea
        v-model="payload.description"
        :value="payload.description"
        :placeholder="t('common.placeholders.describe')"
        @input="update()"
      >
        <template #label>
          {{ t("common.labels.description") }}
        </template>
      </base-textarea>

      <div class="separate" />

      <template v-if="payload.type === 'FILE'">
        <base-select
          v-model="fileType"
          :value="fileType"
          :options="fileTypesOptions"
          @change="updateFile"
        >
          <template #label>
            {{ t("common.labels.type") }}
          </template>
        </base-select>
      </template>

      <template v-if="payload.type === 'RADIO' || payload.type === 'SELECT'">
        <div class="title">
          {{ t("common.labels.options") }}
          <button
            type="button"
            class="btn btn-primary btn-sm"
            @click="addOption()"
          >
            <svg-icon icon="add" size="1rem" />
            {{ t("common.labels.option").toUpperCase() }}
          </button>
        </div>

        <div class="options">
          <template v-for="(item, index) in options" :key="index">
            <div class="item">
              <base-input
                v-model="item.option"
                :value="item.option"
                :placeholder="t('common.labels.name')"
                @input="updateOptions()"
              />

              <base-input
                v-model="item.value"
                :value="item.value"
                :placeholder="t('common.labels.value')"
                @input="updateOptions()"
              />

              <button
                type="button"
                class="btn btn-sm"
                @click="delOption(index)"
              >
                <svg-icon icon="trash" size="1rem" />
              </button>
            </div>
          </template>
          <tempate v-if="options.length === 0">
            <div class="item empty">
              {{ t("common.texts.no_results") }}
            </div>
          </tempate>
        </div>
      </template>

      <template v-if="payload.type === 'CHECKBOX'">
        <div class="title">
          {{ t("common.labels.options") }}
          <button
            type="button"
            class="btn btn-primary btn-sm"
            @click="addOption()"
          >
            <svg-icon icon="add" size="1rem" />
            {{ t("common.labels.option").toUpperCase() }}
          </button>
        </div>

        <div class="options">
          <template v-for="(item, index) in options" :key="index">
            <div class="item">
              <base-input
                v-model="item.option"
                :value="item.option"
                :placeholder="t('common.labels.name')"
                @input="updateOptions()"
              />

              <button
                type="button"
                class="btn btn-sm"
                @click="delOption(index)"
              >
                <svg-icon icon="trash" size="1rem" />
              </button>
            </div>
          </template>
          <tempate v-if="options.length === 0">
            <div class="item empty">
              {{ t("common.texts.no_results") }}
            </div>
          </tempate>
        </div>
      </template>
    </form>
  </section>
</template>

<style lang="scss" scoped>
$width: v-bind("width");

.form-content {
  display: flex;
  flex-direction: column;
  width: calc($width - 0.5rem - 1rem);
  padding-inline-start: 0.5rem;
  padding-inline-end: 1rem;
  gap: 1.25rem;

  .title {
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    font-size: 1.15rem;
    font-weight: bold;
    text-transform: uppercase;
    border-block-end: 1px solid var(--border);
    padding-block-end: 0.5rem;
    padding-inline-start: 0.5rem;
    padding-inline-end: 1rem;
  }

  .separate {
    height: 1.25rem;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .options {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding-block-start: 0.5rem;
    padding-block-end: 1.5rem;

    .item {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.75rem;
      padding-inline-start: 0.5rem;
      padding-inline-end: 1rem;
    }
  }
}
</style>
